import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";

import {
  fetchPandoChargers,
  fetchPandoChargerHistory,
  type PandoHealthRow,
  type PandoStatus,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/ui/data-table";
import { DataTableShell } from "@/components/ui/data-table-shell";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Refresh } from "@/components/icons";
import { formatDuration, formatPercent } from "@voltie/localization-web";
import { useCatalog, useT, type Translate } from "@/app/localization";
import { viewerInstant } from "@/app/displayFormat";


function StatusBadge({ status }: { status: PandoStatus }) {
  const t = useT();
  const variant =
    status === "ok" ? "success"
    : status === "unreachable" || status === "timeout" ? "warning"
    : "destructive";
  return <Badge variant={variant as any}>{t("operator.ops.pando.status_option", { status, raw: status })}</Badge>;
}

/** Check time in the viewer's zone; a value the formatter refuses is shown as sent. */
function formatCheckDate(iso: string | null | undefined): string {
  return viewerInstant(iso) ?? "—";
}

function tmpDisplay(row: PandoHealthRow): string {
  if (row.tmp_used_mb == null || row.tmp_total_mb == null) return "—";
  return `${row.tmp_used_mb} / ${row.tmp_total_mb} MB`;
}

function tmpPercent(row: PandoHealthRow): number | null {
  if (row.tmp_used_mb == null || row.tmp_total_mb == null || row.tmp_total_mb === 0) return null;
  return Math.round((row.tmp_used_mb / row.tmp_total_mb) * 100);
}

function uptimeDisplay(sec: number | null): string {
  if (sec == null) return "—";
  if (sec < 3600) return `${Math.round(sec / 60)}m`;
  if (sec < 86400) return `${Math.round(sec / 3600)}h`;
  return `${Math.round(sec / 86400)}d`;
}

function pandoColumns(t: Translate): ColumnDef<PandoHealthRow>[] {
  return [
  {
    accessorKey: "serial",
    header: t("operator.ops.pando.serial"),
    cell: ({ row }) => <span className="font-mono text-xs">{row.original.serial}</span>,
  },
  {
    id: "last_check",
    header: t("operator.ops.pando.last_check"),
    cell: ({ row }) => {
      const ok = row.original.status === "ok";
      const checkTs = formatCheckDate(row.original.ts);
      const lastOk = row.original.last_ok_ts ? formatCheckDate(row.original.last_ok_ts) : null;
      return (
        <div className="text-xs font-mono leading-tight">
          <div className={ok ? "text-success" : "text-destructive"} title={row.original.ts}>
            {ok ? "✓" : "✗"} {checkTs}
          </div>
          {!ok && (
            <div className="opacity-60">{lastOk ? t("operator.ops.pando.last_ok", { time: lastOk }) : t("operator.ops.pando.last_ok_never")}</div>
          )}
        </div>
      );
    },
  },
  {
    id: "tmp",
    header: t("operator.ops.pando.tmp"),
    cell: ({ row }) => {
      const pct = tmpPercent(row.original);
      const danger = pct !== null && pct >= 80;
      return (
        <span className={["text-xs", danger ? "text-destructive font-semibold" : ""].join(" ")}>
          {tmpDisplay(row.original)}
          {pct !== null && <span className="opacity-60"> ({formatPercent(pct / 100, 0)})</span>}
        </span>
      );
    },
  },
  {
    id: "logs",
    header: t("operator.ops.pando.logs_dir"),
    cell: ({ row }) => {
      const v = row.original.logs_dir_mb;
      if (v == null) return <span className="text-xs opacity-60">—</span>;
      const danger = v >= 100;
      return (
        <span className={["text-xs", danger ? "text-destructive font-semibold" : ""].join(" ")}>
          {v} MB
        </span>
      );
    },
  },
  {
    id: "uptime",
    header: t("operator.ops.pando.uptime"),
    cell: ({ row }) => <span className="text-xs">{uptimeDisplay(row.original.uptime_sec)}</span>,
  },
  {
    accessorKey: "action",
    header: t("operator.ops.pando.last_action"),
    cell: ({ row }) => <span className="text-xs">{row.original.action}</span>,
  },
  {
    accessorKey: "status",
    header: t("operator.ops.pando.status"),
    cell: ({ row }) => <StatusBadge status={row.original.status} />,
  },
  ];
}


export function PandoChargersPage() {
  const { t, catalog } = useCatalog();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(() => pandoColumns(t), [catalog]);
  const [openSerial, setOpenSerial] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["pandoChargers"],
    queryFn: fetchPandoChargers,
    refetchInterval: 30_000,
  });

  const rows = q.data?.chargers ?? [];

  return (
    <div className="space-y-5 pt-[86px]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t("operator.ops.pando.title")}</h1>
          <div className="text-sm opacity-70">{t("operator.ops.pando.subtitle")}</div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => q.refetch()}>
            <Refresh className="h-4 w-4" /> {t("common.action.refresh")}
          </Button>
        </div>
      </div>

      {q.isError ? (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          {t("operator.ops.pando.load_failed")}
        </div>
      ) : (
        <DataTableShell>
          <DataTable
            columns={columns}
            data={rows}
            isLoading={q.isLoading}
            emptyMessage={t("operator.ops.pando.empty", { command: "python -m scripts.pando_ssh_ops probe --all" })}
            onRowClick={(row) => setOpenSerial(row.serial)}
          />
        </DataTableShell>
      )}

      <div className="text-center text-xs opacity-50">
        {t("operator.ops.pando.footer", { count: rows.length, interval: formatDuration(30, "seconds") })}
      </div>

      <HistoryDialog serial={openSerial} onClose={() => setOpenSerial(null)} />
    </div>
  );
}


function HistoryDialog({ serial, onClose }: { serial: string | null; onClose: () => void }) {
  const t = useT();
  const q = useQuery({
    queryKey: ["pandoChargerHistory", serial],
    queryFn: () => fetchPandoChargerHistory(serial!, 50),
    enabled: !!serial,
  });

  return (
    <Dialog open={!!serial} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-mono">{serial}</DialogTitle>
        </DialogHeader>
        {q.isLoading ? (
          <div className="text-sm opacity-60">{t("operator.ops.pando.history_loading")}</div>
        ) : q.isError ? (
          <div className="text-sm text-destructive">{t("operator.ops.pando.history_failed")}</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-left opacity-70">
              <tr>
                <th className="py-1 pr-3">{t("operator.ops.pando.history_when")}</th>
                <th className="py-1 pr-3">{t("operator.ops.pando.history_action")}</th>
                <th className="py-1 pr-3">{t("operator.ops.pando.status")}</th>
                <th className="py-1 pr-3">{t("operator.ops.pando.tmp")}</th>
                <th className="py-1 pr-3">{t("operator.ops.pando.history_logs")}</th>
                <th className="py-1 pr-3">{t("operator.ops.pando.history_rotate")}</th>
                <th className="py-1 pr-3">{t("operator.ops.pando.history_error")}</th>
              </tr>
            </thead>
            <tbody>
              {(q.data?.history ?? []).map((r, i) => (
                <tr key={i} className="border-t border-border/40">
                  <td className="py-1 pr-3 font-mono whitespace-nowrap">{r.ts}</td>
                  <td className="py-1 pr-3">{r.action}</td>
                  <td className="py-1 pr-3"><StatusBadge status={r.status} /></td>
                  <td className="py-1 pr-3">{tmpDisplay(r)}</td>
                  <td className="py-1 pr-3">{r.logs_dir_mb == null ? "—" : `${r.logs_dir_mb} MB`}</td>
                  <td className="py-1 pr-3">
                    {r.before_mb != null && r.after_mb != null
                      ? `${r.before_mb}→${r.after_mb} MB`
                      : "—"}
                  </td>
                  <td className="py-1 pr-3 text-destructive">{r.error ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DialogContent>
    </Dialog>
  );
}
